import type { Moment } from 'moment';
import moment from 'moment';
import { observable } from 'mobx';

export type BizEkinetBookingInventoryItemResponseArgs = {
  biz_ekinet_booking_inventory_pair_id: number;
  biz_ekinet_booking_inventory_pair_ai_operator_reservation_id: number | null;
  biz_ekinet_booking_inventory_pair_biz_ekinet_reservation_id: number | null;
  biz_ekinet_booking_inventory_pair_is_inventoried: string | null;
  biz_ekinet_booking_inventory_pair_inventoried_by: string | null;
  biz_ekinet_booking_inventory_pair_inventoried_at: string | null;
  ai_operator_reservation_id: number | null;
  ai_operator_reservation_created_at: string | null;
  ai_operator_reservation_status: string | null;
  ai_operator_reservation_is_auto_processed: boolean | null;
  biz_ekinet_reservation_id: number | null;
  biz_ekinet_reservation_created_at: string | null;
  reservation_number: string | null;
  target_date: string | null;
  price: number | null;
  reservation_date: string | null;
  ai_operator_is_inventoried: string | null;
  ai_operator_inventoried_by: string | null;
  ai_operator_inventoried_at: string | null;
  ai_operator_trip_id: number | null;
  ai_operator_trace_id: number | null;
  ai_operator_order_item_id: number | null;
  biz_ekinet_is_inventoried: string | null;
  biz_ekinet_inventoried_by: string | null;
  biz_ekinet_inventoried_at: string | null;
  biz_ekinet_trip_id: number | null;
  biz_ekinet_trace_id: number | null;
  biz_ekinet_order_item_id: number | null;
  last_log_message: string | null;
  last_log_message_arranger: string | null;
};

export type BizEkinetBookingInventoryItemArgs = {
  bizEkinetBookingInventoryPairId: number;
  bizEkinetBookingInventoryPairAiOperatorReservationId: number | null;
  bizEkinetBookingInventoryPairBizEkinetReservationId: number | null;
  bizEkinetBookingInventoryPairIsInventoried: string | null;
  bizEkinetBookingInventoryPairInventoriedBy: string | null;
  bizEkinetBookingInventoryPairInventoriedAt: Moment | null;
  aiOperatorReservationId: number | null;
  aiOperatorReservationCreatedAt: Moment | null;
  aiOperatorReservationStatus: string | null;
  aiOperatorReservationIsAutoProcessed: boolean | null;
  bizEkinetReservationId: number | null;
  bizEkinetReservationCreatedAt: Moment | null;
  reservationNumber: string | null;
  targetDate: Moment | null;
  price: number | null;
  reservationDate: Moment | null;
  aiOperatorIsInventoried: string | null;
  aiOperatorInventoriedBy: string | null;
  aiOperatorInventoriedAt: Moment | null;
  aiOperatorTripId: number | null;
  aiOperatorTraceId: number | null;
  aiOperatorOrderItemId: number | null;
  bizEkinetIsInventoried: string | null;
  bizEkinetInventoriedBy: string | null;
  bizEkinetInventoriedAt: Moment | null;
  bizEkinetTripId: number | null;
  bizEkinetTraceId: number | null;
  bizEkinetOrderItemId: number | null;
  lastLogMessage: string | null;
  lastLogMessageArranger: string | null;
};

export const convertBizEkinetBookingInventoryItemResponseToArgs = (
  response: BizEkinetBookingInventoryItemResponseArgs
): BizEkinetBookingInventoryItemArgs => {
  return {
    bizEkinetBookingInventoryPairId: response.biz_ekinet_booking_inventory_pair_id,
    bizEkinetBookingInventoryPairAiOperatorReservationId:
      response.biz_ekinet_booking_inventory_pair_ai_operator_reservation_id,
    bizEkinetBookingInventoryPairBizEkinetReservationId:
      response.biz_ekinet_booking_inventory_pair_biz_ekinet_reservation_id,
    bizEkinetBookingInventoryPairIsInventoried: response.biz_ekinet_booking_inventory_pair_is_inventoried,
    bizEkinetBookingInventoryPairInventoriedBy: response.biz_ekinet_booking_inventory_pair_inventoried_by,
    bizEkinetBookingInventoryPairInventoriedAt: response.biz_ekinet_booking_inventory_pair_inventoried_at
      ? moment(response.biz_ekinet_booking_inventory_pair_inventoried_at)
      : null,
    aiOperatorReservationId: response.ai_operator_reservation_id,
    aiOperatorReservationCreatedAt: response.ai_operator_reservation_created_at
      ? moment(response.ai_operator_reservation_created_at)
      : null,
    aiOperatorReservationStatus: response.ai_operator_reservation_status,
    aiOperatorReservationIsAutoProcessed: response.ai_operator_reservation_is_auto_processed,
    bizEkinetReservationId: response.biz_ekinet_reservation_id,
    bizEkinetReservationCreatedAt: response.biz_ekinet_reservation_created_at
      ? moment(response.biz_ekinet_reservation_created_at)
      : null,
    reservationNumber: response.reservation_number,
    targetDate: response.target_date ? moment(response.target_date) : null,
    price: response.price,
    reservationDate: response.reservation_date ? moment(response.reservation_date) : null,
    aiOperatorIsInventoried: response.ai_operator_is_inventoried,
    aiOperatorInventoriedBy: response.ai_operator_inventoried_by,
    aiOperatorInventoriedAt: response.ai_operator_inventoried_at
      ? moment(response.ai_operator_inventoried_at)
      : null,
    aiOperatorTripId: response.ai_operator_trip_id,
    aiOperatorTraceId: response.ai_operator_trace_id,
    aiOperatorOrderItemId: response.ai_operator_order_item_id,
    bizEkinetIsInventoried: response.biz_ekinet_is_inventoried,
    bizEkinetInventoriedBy: response.biz_ekinet_inventoried_by,
    bizEkinetInventoriedAt: response.biz_ekinet_inventoried_at ? moment(response.biz_ekinet_inventoried_at) : null,
    bizEkinetTripId: response.biz_ekinet_trip_id,
    bizEkinetTraceId: response.biz_ekinet_trace_id,
    bizEkinetOrderItemId: response.biz_ekinet_order_item_id,
    lastLogMessage: response.last_log_message,
    lastLogMessageArranger: response.last_log_message_arranger
  };
};

export default class BizEkinetBookingInventoryItem {
  @observable bizEkinetBookingInventoryPairId: number;
  @observable bizEkinetBookingInventoryPairAiOperatorReservationId: number | null;
  @observable bizEkinetBookingInventoryPairBizEkinetReservationId: number | null;
  @observable bizEkinetBookingInventoryPairIsInventoried: string | null;
  @observable bizEkinetBookingInventoryPairInventoriedBy: string | null;
  @observable bizEkinetBookingInventoryPairInventoriedAt: Moment | null;
  @observable aiOperatorReservationId: number | null;
  @observable aiOperatorReservationCreatedAt: Moment | null;
  @observable aiOperatorReservationStatus: string | null;
  @observable aiOperatorReservationIsAutoProcessed: boolean | null;
  @observable bizEkinetReservationId: number | null;
  @observable bizEkinetReservationCreatedAt: Moment | null;
  @observable reservationNumber: string | null;
  @observable targetDate: Moment | null;
  @observable price: number | null;
  @observable reservationDate: Moment | null;
  @observable aiOperatorIsInventoried: string | null;
  @observable aiOperatorInventoriedBy: string | null;
  @observable aiOperatorInventoriedAt: Moment | null;
  @observable aiOperatorTripId: number | null;
  @observable aiOperatorTraceId: number | null;
  @observable aiOperatorOrderItemId: number | null;
  @observable bizEkinetIsInventoried: string | null;
  @observable bizEkinetInventoriedBy: string | null;
  @observable bizEkinetInventoriedAt: Moment | null;
  @observable bizEkinetTripId: number | null;
  @observable bizEkinetTraceId: number | null;
  @observable bizEkinetOrderItemId: number | null;
  @observable lastLogMessage: string | null;
  @observable lastLogMessageArranger: string | null;

  constructor(args: BizEkinetBookingInventoryItemArgs) {
    this.bizEkinetBookingInventoryPairId = args.bizEkinetBookingInventoryPairId;
    this.bizEkinetBookingInventoryPairAiOperatorReservationId =
      args.bizEkinetBookingInventoryPairAiOperatorReservationId;
    this.bizEkinetBookingInventoryPairBizEkinetReservationId =
      args.bizEkinetBookingInventoryPairBizEkinetReservationId;
    this.bizEkinetBookingInventoryPairIsInventoried = args.bizEkinetBookingInventoryPairIsInventoried;
    this.bizEkinetBookingInventoryPairInventoriedBy = args.bizEkinetBookingInventoryPairInventoriedBy;
    this.bizEkinetBookingInventoryPairInventoriedAt = args.bizEkinetBookingInventoryPairInventoriedAt
      ? moment(args.bizEkinetBookingInventoryPairInventoriedAt)
      : null;
    this.aiOperatorReservationId = args.aiOperatorReservationId;
    this.aiOperatorReservationCreatedAt = args.aiOperatorReservationCreatedAt
      ? moment(args.aiOperatorReservationCreatedAt)
      : null;
    this.aiOperatorReservationStatus = args.aiOperatorReservationStatus;
    this.aiOperatorReservationIsAutoProcessed = args.aiOperatorReservationIsAutoProcessed;
    this.bizEkinetReservationId = args.bizEkinetReservationId;
    this.bizEkinetReservationCreatedAt = args.bizEkinetReservationCreatedAt
      ? moment(args.bizEkinetReservationCreatedAt)
      : null;
    this.reservationNumber = args.reservationNumber;
    this.targetDate = args.targetDate ? moment(args.targetDate) : null;
    this.price = args.price;
    this.reservationDate = args.reservationDate ? moment(args.reservationDate) : null;
    this.aiOperatorIsInventoried = args.aiOperatorIsInventoried;
    this.aiOperatorInventoriedBy = args.aiOperatorInventoriedBy;
    this.aiOperatorInventoriedAt = args.aiOperatorInventoriedAt ? moment(args.aiOperatorInventoriedAt) : null;
    this.aiOperatorTripId = args.aiOperatorTripId;
    this.aiOperatorTraceId = args.aiOperatorTraceId;
    this.aiOperatorOrderItemId = args.aiOperatorOrderItemId;
    this.bizEkinetIsInventoried = args.bizEkinetIsInventoried;
    this.bizEkinetInventoriedBy = args.bizEkinetInventoriedBy;
    this.bizEkinetInventoriedAt = args.bizEkinetInventoriedAt ? moment(args.bizEkinetInventoriedAt) : null;
    this.bizEkinetTripId = args.bizEkinetTripId;
    this.bizEkinetTraceId = args.bizEkinetTraceId;
    this.bizEkinetOrderItemId = args.bizEkinetOrderItemId;
    this.lastLogMessage = args.lastLogMessage;
    this.lastLogMessageArranger = args.lastLogMessageArranger;
  }
}
